import React, { useEffect, useState, useContext } from 'react';
import {
  BookOpen, Users, CheckSquare, TrendingUp, RefreshCw, Stethoscope,
  CheckCircle, XCircle, LayoutDashboard, List, Settings2,
} from 'lucide-react';
import { YearContext } from '../components/Layout/Layout';
import { useAuth } from '../context/AuthContext';
import StatCard from '../components/Cards/StatCard';
import FilterBar from '../components/Common/FilterBar';
import ImportExport from '../components/Common/ImportExport';
import DataTable from '../components/Common/DataTable';
import { BarChartComponent, LineChartComponent, PieChartComponent } from '../components/Charts/ChartComponents';
import { homeworkAPI, erpAPI } from '../services/api';
import { PageLoader } from '../components/Common/LoadingSpinner';
import EmailNotificationSetup       from '../components/Common/EmailNotificationSetup';
import TelegramNotificationSetup    from '../components/Common/TelegramNotificationSetup';
import NotificationThresholdCard    from '../components/Common/NotificationThresholdCard';
import toast from 'react-hot-toast';

const BASE_TABS = [
  { id: 'overview',       label: 'Overview',       icon: LayoutDashboard },
  { id: 'teacher_wise',   label: 'Teacher-wise',   icon: Users           },
  { id: 'monthly_trend',  label: 'Monthly Trend',  icon: TrendingUp      },
  { id: 'data_table',     label: 'Data Table',     icon: List            },
];
const SETUP_TAB = { id: 'setup', label: 'Setup', icon: Settings2 };

export default function Homework() {
  const { yearId, refreshKey } = useContext(YearContext);
  const { isAdmin, isSuperAdmin } = useAuth();
  const canSetup = isAdmin || isSuperAdmin;
  const TABS = [...BASE_TABS, ...(canSetup ? [SETUP_TAB] : [])];

  const [tab,      setTab]    = useState('overview');
  const [filters,  setFilters] = useState({ month: '', week: '', startDate: '', endDate: '' });
  const [stats,    setStats]   = useState(null);
  const [list,     setList]    = useState([]);
  const [loading,  setLoading] = useState(true);
  const [syncing,       setSyncing]       = useState(false);
  const [syncStartDate, setSyncStartDate] = useState('');
  const [syncEndDate,   setSyncEndDate]   = useState('');
  const [syncResult,    setSyncResult]    = useState(null);
  const [diagnosing,    setDiagnosing]    = useState(false);
  const [diagnoseResult,setDiagnoseResult]= useState(null);

  const handleDiagnose = async () => {
    setDiagnosing(true);
    setDiagnoseResult(null);
    try {
      const { data } = await erpAPI.diagnoseHomework();
      setDiagnoseResult(data);
    } catch (e) {
      setDiagnoseResult({ error: e.response?.data?.error || 'Diagnose failed' });
    } finally {
      setDiagnosing(false);
    }
  };

  const handleErpSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const body = {};
      if (syncStartDate) body.startDate = syncStartDate;
      if (syncEndDate)   body.endDate   = syncEndDate;
      const { data } = await erpAPI.syncHomework(body);
      setSyncResult(data);
      const curParams = { academicYearId: yearId, ...filters };
      const [s, l] = await Promise.all([
        homeworkAPI.getStats(curParams),
        homeworkAPI.getList({ ...curParams, limit: 50 }),
      ]);
      setStats(s.data);
      setList(l.data.data || []);
    } catch (e) {
      setSyncResult({ error: e.response?.data?.error || 'ERP sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  const params = { academicYearId: yearId, ...filters };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      homeworkAPI.getStats(params),
      homeworkAPI.getList({ ...params, limit: 50 }),
    ]).then(([s, l]) => {
      setStats(s.data);
      setList(l.data.data || []);
    }).catch(console.error)
    .finally(() => setLoading(false));
  }, [yearId, refreshKey, JSON.stringify(filters)]); // eslint-disable-line

  const teacherData  = (stats?.byTeacher || []).slice(0, 10).map(t => ({ name: t.teacher.split(' ')[0], count: t.assignments, pct: t.pct }));
  const subjectData  = (stats?.bySubject || []).map(s => ({ name: s.subject, value: s.assignments }));
  const monthlyData  = (stats?.monthlyTrend || []).map(m => ({ month: `M${m.month}`, pct: m.submission_pct, count: m.assignments }));

  const columns = [
    { key: 'teacher',        label: 'Teacher',    sortable: true },
    { key: 'class_section',  label: 'Class',      sortable: true },
    { key: 'subject',        label: 'Subject',    sortable: true },
    { key: 'title',          label: 'Title' },
    { key: 'assigned_date',  label: 'Assigned',   sortable: true, render: v => v ? new Date(v).toLocaleDateString('en-IN') : '—' },
    { key: 'due_date',       label: 'Due',        sortable: true, render: v => v ? new Date(v).toLocaleDateString('en-IN') : '—' },
    { key: 'total_students', label: 'Students',   sortable: true },
    { key: 'submitted_count',label: 'Submitted',  sortable: true },
    { key: 'completion_pct', label: 'Completion', sortable: true,
      render: v => (
        <span className={`text-xs font-semibold ${v>=80?'text-emerald-600':v>=60?'text-amber-600':'text-red-600'}`}>{v}%</span>
      )
    },
  ];

  if (loading && tab !== 'setup') return <PageLoader />;

  return (
    <div className="space-y-5 animate-fade-in">

      {/* ── Page header with tab navigation ── */}
      <div className="space-y-2.5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-display font-bold text-slate-800">Homework</h2>
            <p className="text-slate-500 text-sm mt-0.5">Track homework assignments and submission rates</p>
          </div>

          {/* Tab switcher */}
          <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm shrink-0">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium transition-colors ${
                  tab === t.id ? 'bg-primary-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}>
                <t.icon size={13} /> {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Second row — Import/Export (data table tab only) */}
        {tab === 'data_table' && (
          <div className="flex justify-end">
            <ImportExport
              compact
              module="Homework"
              onImport={fd => homeworkAPI.import(fd, { academicYearId: yearId })}
              onExport={() => homeworkAPI.export(params)}
              onSampleTemplate={homeworkAPI.sampleTemplate}
            />
          </div>
        )}
      </div>

      {/* ── Overview ────────────────────────────────────────────── */}
      {tab === 'overview' && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard title="Total Assignments"  value={stats?.overall?.total_assignments || 0}  icon={BookOpen}    color="blue"   />
            <StatCard title="Total Students"     value={stats?.overall?.total_students || 0}      icon={Users}       color="purple" />
            <StatCard title="Total Submitted"    value={stats?.overall?.total_submitted || 0}     icon={CheckSquare} color="green"  />
            <StatCard title="Submission Rate"    value={`${stats?.overall?.submission_pct || 0}%`} icon={TrendingUp} color="yellow" />
          </div>

          <FilterBar filters={filters} onChange={setFilters} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <BarChartComponent title="Teacher-wise Homework Count" data={teacherData} xKey="name"
              bars={[{ key: 'count', label: 'Assignments', color: '#0ea5e9' }]} />
            <PieChartComponent title="Subject-wise Homework Distribution" data={subjectData} donut />
          </div>
        </>
      )}

      {/* ── Teacher-wise ─────────────────────────────────────────── */}
      {tab === 'teacher_wise' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <BarChartComponent title="Assignments by Teacher" data={teacherData} xKey="name"
            bars={[{ key: 'count', label: 'Assignments', color: '#0ea5e9' }]} />
          <BarChartComponent title="Submission % by Teacher" data={teacherData} xKey="name"
            bars={[{ key: 'pct', label: 'Submission %', color: '#10b981' }]} />
        </div>
      )}

      {/* ── Monthly Trend ─────────────────────────────────────────── */}
      {tab === 'monthly_trend' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <LineChartComponent title="Monthly Submission Rate" data={monthlyData} xKey="month"
            lines={[{ key: 'pct', label: 'Submission %', color: '#f59e0b' }]} />
          <BarChartComponent title="Monthly Assignment Count" data={monthlyData} xKey="month"
            bars={[{ key: 'count', label: 'Assignments', color: '#8b5cf6' }]} />
        </div>
      )}

      {/* ── Data Table ───────────────────────────────────────────── */}
      {tab === 'data_table' && (
        <>
          <FilterBar filters={filters} onChange={setFilters} />
          <DataTable columns={columns} data={list} loading={loading} emptyText="No homework data found" />
        </>
      )}

      {/* ── Setup ────────────────────────────────────────────────── */}
      {tab === 'setup' && canSetup && (
        <div className="space-y-5">
          {/* ERP Sync */}
          <div className="glass-card p-6">
            <h3 className="text-sm font-bold text-slate-700 mb-1 flex items-center gap-2">
              <RefreshCw size={15} className="text-teal-600" />
              Sync Homework from ERP
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Pull homework assignments from Edunext ERP. Select a date range to limit the sync period, or leave blank to fetch all available records.
            </p>
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">From Date</label>
                <input
                  type="date"
                  value={syncStartDate}
                  onChange={e => setSyncStartDate(e.target.value)}
                  className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">To Date</label>
                <input
                  type="date"
                  value={syncEndDate}
                  onChange={e => setSyncEndDate(e.target.value)}
                  className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                />
              </div>
              <button
                onClick={handleErpSync}
                disabled={syncing}
                className="flex items-center gap-2 px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 shadow-sm"
              >
                <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
                {syncing ? 'Syncing…' : 'Sync from ERP'}
              </button>
            </div>
            {syncResult && (
              <div className={`rounded-xl p-4 text-sm ${syncResult.error ? 'bg-red-50 border border-red-100 text-red-700' : 'bg-teal-50 border border-teal-100 text-teal-700'}`}>
                {syncResult.error
                  ? syncResult.error
                  : `${syncResult.message}`
                }
                {!syncResult.error && (
                  <div className="mt-1 text-xs opacity-80">
                    {syncResult.inserted ?? 0} new &nbsp;·&nbsp; {syncResult.updated ?? 0} updated &nbsp;·&nbsp; {syncResult.skipped ?? 0} skipped &nbsp;·&nbsp; {syncResult.total ?? 0} total from ERP
                  </div>
                )}
                {syncResult.unmatched?.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs opacity-70">{syncResult.unmatched.length} records could not be matched</summary>
                    <ul className="mt-1 space-y-0.5 text-xs opacity-80">
                      {syncResult.unmatched.map((u, i) => (
                        <li key={i}>
                          <span className="font-medium">{u.title}:</span> {u.reason}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}

            {/* Diagnose */}
            <div className="mt-5 pt-5 border-t border-slate-100">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-xs font-semibold text-slate-600">ERP Endpoint Diagnostic</p>
                  <p className="text-xs text-slate-400 mt-0.5">Probe all known Edunext homework endpoints to find which one returns data.</p>
                </div>
                <button
                  onClick={handleDiagnose}
                  disabled={diagnosing}
                  className="flex items-center gap-2 px-4 py-1.5 border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  <Stethoscope size={13} className={diagnosing ? 'animate-pulse' : ''} />
                  {diagnosing ? 'Probing…' : 'Run Diagnostic'}
                </button>
              </div>
              {diagnoseResult && (
                diagnoseResult.error
                  ? <p className="text-xs text-red-600">{diagnoseResult.error}</p>
                  : (
                    <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                      {diagnoseResult.best
                        ? (
                          <div className="px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700 font-medium">
                            ✓ Found: {diagnoseResult.bestLabel}
                          </div>
                        ) : (
                          <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                            No endpoint returned data yet — see details below.
                          </div>
                        )
                      }
                      {diagnoseResult.summary?.mvcRedirectTo?.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-slate-500 mb-1">MVC Session</p>
                          {diagnoseResult.summary.mvcRedirectTo.map(r => (
                            <div key={r.id} className="flex items-start gap-2 px-2 py-1 text-xs text-slate-500">
                              <XCircle size={11} className="mt-0.5 flex-shrink-0 text-slate-300" />
                              <span className="flex-1">{r.label}</span>
                              <span className="font-mono text-slate-400">
                                {r.status}{r.location ? ` → ${r.location.split('/').slice(-2).join('/')}` : ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div>
                        <p className="text-xs font-semibold text-slate-500 mb-1">
                          Host sweep (m1–m8) — non-404 responses
                          {diagnoseResult.summary?.hostsWithNon404?.length === 0 && (
                            <span className="text-red-500 font-normal ml-1">all 404 — homework is not a REST endpoint</span>
                          )}
                        </p>
                        {(diagnoseResult.summary?.hostsWithNon404 || []).length > 0
                          ? (diagnoseResult.summary.hostsWithNon404.map(r => (
                            <div key={r.id} className={`flex items-start gap-2 px-2 py-1 rounded text-xs ${r.rowCount > 0 ? 'bg-emerald-50 text-emerald-700' : 'text-sky-700'}`}>
                              {r.rowCount > 0 ? <CheckCircle size={11} className="mt-0.5 flex-shrink-0 text-emerald-500" /> : <XCircle size={11} className="mt-0.5 flex-shrink-0 text-sky-400" />}
                              <span className="flex-1">{r.label}</span>
                              <span className="font-mono text-slate-500">{r.status}{r.errorMsg ? ` · ${r.errorMsg.slice(0, 40)}` : ''}</span>
                            </div>
                          )))
                          : <p className="text-xs text-slate-400 px-2">All REST paths return 404 on every host.</p>
                        }
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-500 mb-1">
                          advanceTableView error messages (all view names tried)
                        </p>
                        {(diagnoseResult.summary?.atvUniqueErrors || []).length > 0
                          ? diagnoseResult.summary.atvUniqueErrors.map((msg, i) => (
                            <div key={i} className="px-2 py-1 text-xs text-orange-700 bg-orange-50 rounded mb-1 font-mono">{msg}</div>
                          ))
                          : <p className="text-xs text-slate-400 px-2">No error message returned (response body had no message field).</p>
                        }
                        {(diagnoseResult.atvResults || []).filter(r => r.rowCount > 0).map(r => (
                          <div key={r.id} className="flex items-start gap-2 px-2 py-1 rounded bg-emerald-50 text-emerald-700 text-xs mt-1">
                            <CheckCircle size={11} className="mt-0.5 flex-shrink-0 text-emerald-500" />
                            <span>{r.label} — {r.rowCount} rows</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
              )}
            </div>
          </div>

          <NotificationThresholdCard
            module="homework"
            yearId={yearId}
            title="Submission Rate Thresholds"
            thresholds={[
              {
                key: 'min_submission_pct',
                label: 'Minimum weekly submission rate (%)',
                description: 'Send a notification if the homework submission rate for the week falls below this value.',
                defaultValue: 70,
                min: 0,
                max: 100,
                suffix: '% submission rate',
              },
            ]}
          />
          <EmailNotificationSetup module="homework" moduleLabel="Homework" yearId={yearId} />
          <TelegramNotificationSetup module="homework" moduleLabel="Homework" yearId={yearId} />
        </div>
      )}
    </div>
  );
}
